<script setup lang="ts">
import type { VNode } from 'vue';
import { computed, ref } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppLinkButton } from '@libs/ui';

const { t } = i18n.global;

interface Limits {
  collapsed: number;
  extended: number;
}

interface BaseProps {
  borderSeparator: boolean;
  title?: string;
}

interface ExtendableProps extends BaseProps {
  extendable: boolean;
  limits: Limits;
  items: unknown[];
}

interface NonExtendableProps extends BaseProps {
  extendable?: never;
  limits?: never;
  items?: unknown[];
}

type HotelFiltersSectionProps = ExtendableProps | NonExtendableProps;

const props = defineProps<HotelFiltersSectionProps>();

defineSlots<{
  default: (props: { items: unknown[] }) => VNode;
}>();

const isExpanded = ref<boolean>(false);
const DEFAULT_MAX_COLLAPSED = 10;

function toggleExpand(): void {
  isExpanded.value = !isExpanded.value;
}

const visibleItems = computed<unknown[]>(() => {
  if (!props.items) {
    return [];
  }
  if (!props.extendable || isExpanded.value) {
    return props.limits?.extended
      ? props.items.slice(0, props.limits.extended)
      : props.items;
  }
  return props.items.slice(0, props.limits?.collapsed ?? DEFAULT_MAX_COLLAPSED);
});
</script>

<template>
  <div
    class="hotel-filters-section"
    :class="{ 'border-separator': props.borderSeparator }"
  >
    <div v-if="props.title" class="title">{{ props.title }}</div>
    <slot :items="visibleItems" />
    <AppLinkButton
      v-if="
        props.extendable &&
        props.items &&
        props.items.length > (props.limits?.collapsed ?? DEFAULT_MAX_COLLAPSED)
      "
      :text="isExpanded ? t('planner.show_less') : t('planner.show_all')"
      :left-icon="isExpanded ? 'chevronUp' : 'chevronDown'"
      size="m"
      @click="toggleExpand"
    />
  </div>
</template>

<style scoped lang="scss">
.hotel-filters-section {
  padding-bottom: var(--spacing-32);

  display: flex;
  flex-direction: column;
  gap: var(--spacing-16);

  &.border-separator {
    border-bottom: 1px solid var(--color-neutral-30);
    margin-bottom: var(--spacing-32);
  }

  .title {
    @include font-label-l-bold;
    color: var(--color-neutral-70);
  }
}
</style>
